import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useDispatch } from "react-redux";
import {
  createAdvertismentService,
  getAdvertisementsService,
  updateAdvertisementService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const CampaignModal = ({ isOpen, onClose, campaign }) => {
  
  const {t} = useTranslation();

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    image: null,
    imagePreview: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (campaign) {
      setFormData({
        image: campaign?.image ? campaign.image[0] : null,
        imagePreview: campaign?.image ? campaign.image : null, // Set the URL for preview
      });
    } else {
      setFormData({
        image: null,
        imagePreview: null,
      });
    }
    setErrors({});
  }, [campaign, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newMediaFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
      };

      // Validate file type and size
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          image: "Invalid file type. Only JPEG, PNG, and GIF are allowed.",
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          image: "File size must be less than 5MB",
        }));
        return;
      }

      // Clear any previous errors
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: "" }));
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: newMediaFile, // Set the new media file for preview
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeMediaFile = () => {
    handleInputChange("image", null); // Clear the mediaFile
    handleInputChange("imagePreview", null); // Clear the imagePreview
  };

  const handleSave = () => {
    if (!formData?.image) {
      setErrors({ image: "A media file is required" });
      return;
    }
    console.log(formData);
    try {
      const formData2 = new FormData();
      formData2.append("image", formData.image);
      
      if (campaign) {
        formData2.append("_method", "PUT");
        dispatch(
          updateAdvertisementService({
            id: campaign?.id,
            formData: formData2,
          })
        ).then((response) => {
          if (
            response?.payload?.status === 200 ||
            response?.payload?.status === 201
          ) {
            toast("Updated successfully");
            dispatch(getAdvertisementsService({ page: 1 }));
            onClose();
          }
        });
      } else {
        dispatch(createAdvertismentService({ data: formData2 })).then(
          (response) => {
            if (
              response?.payload?.status === 200 ||
              response?.payload?.status === 201
            ) {
              toast("Added successfully");
              dispatch(getAdvertisementsService({ page: 1 }));
              onClose();
            }
          }
        );
      }
    } finally {
      // setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {campaign ? t("editCampaign") : t("createNewCampaign")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t("uploadCampaignMedia")}
            </p>
          </div>
          <button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("uploadMediaFile")}
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <Icon
                  name="Upload"
                  size={32}
                  className="text-gray-400 dark:text-gray-500 mx-auto mb-2"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                 {t("dragDropOrClick")}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="media-upload"
                />
                <Button
                  variant="outline"
                  onClick={() =>
                    document.getElementById("media-upload")?.click()
                  }
                >
                 {t("selectFile")}
                </Button>
              </div>
              {errors?.image && (
                <p className="text-sm text-red-600 mt-1">{errors?.image}</p>
              )}
            </div>

            {formData?.imagePreview && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("uploadedFile")}
                </label>
                <div className="relative border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                  <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded overflow-hidden mb-2">
                    <img
                      src={formData?.imagePreview?.url || formData.imagePreview}
                      alt={formData?.imagePreview?.name ||   t("uploadedFile")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
                    {formData?.imagePreview?.name ||  t("uploadedFile")}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeMediaFile}
                    className="absolute top-1 right-1 h-6 w-6 text-red-600"
                  >
                    <Icon name="X" size={12} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <button className="mr-4 ml-4" variant="ghost" onClick={onClose}>
              {t("cancel")}
            </button>
            <Button onClick={handleSave}>
              {campaign ? t("updateCampaign"): t("launchCampaign")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;
